import React, { useState } from 'react';
import {
  Box, Heading, Text, Badge, Flex, Code, Divider, VStack, HStack,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Tabs, TabList, TabPanels, Tab, TabPanel,
} from '@chakra-ui/react';

const METHOD_COLOR = { GET: 'green', POST: 'blue', PUT: 'orange', DELETE: 'red' };

function EndpointCard({ method, path, description, auth, request, response }) {
  return (
    <AccordionItem border="1px solid" borderColor="gray.100" borderRadius="lg" mb={3} overflow="hidden">
      <AccordionButton px={5} py={4} _hover={{ bg: 'gray.50' }}>
        <HStack flex={1} spacing={3} align="center">
          <Badge colorScheme={METHOD_COLOR[method] || 'gray'} fontSize="xs" fontWeight="bold" px={2} py={1}>
            {method}
          </Badge>
          <Code fontSize="sm" bg="transparent" color="gray.700" fontWeight="500">{path}</Code>
        </HStack>
        <Text fontSize="sm" color="gray.500" mr={3} display={{ base: 'none', md: 'block' }}>{description}</Text>
        <AccordionIcon color="gray.400" />
      </AccordionButton>
      <AccordionPanel px={5} pb={5} bg="gray.50">
        {description && <Text color="gray.600" fontSize="sm" mb={4}>{description}</Text>}
        {auth && (
          <Box mb={4}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={2}>Auth</Text>
            <Code fontSize="xs" bg="purple.50" color="purple.700" px={3} py={2} borderRadius="md" display="block">
              {auth}
            </Code>
          </Box>
        )}
        {request && (
          <Box mb={4}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={2}>Request body</Text>
            <Code fontSize="xs" bg="white" color="gray.800" px={3} py={3} borderRadius="md" display="block"
              whiteSpace="pre" borderWidth="1px" borderColor="gray.200">
              {request}
            </Code>
          </Box>
        )}
        {response && (
          <Box>
            <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={2}>Response</Text>
            <Code fontSize="xs" bg="white" color="gray.800" px={3} py={3} borderRadius="md" display="block"
              whiteSpace="pre" borderWidth="1px" borderColor="gray.200">
              {response}
            </Code>
          </Box>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
}

function CodeBlock({ children, lang }) {
  return (
    <Box
      as="pre"
      bg="gray.900"
      color="gray.100"
      fontSize="xs"
      p={4}
      borderRadius="lg"
      overflowX="auto"
      fontFamily="mono"
      lineHeight="1.6"
    >
      {children}
    </Box>
  );
}

const PORTAL_SDK = `// portal/src/lib/telemetry.js
import { init, track } from './lib/telemetry';

// Call once after login, passing user context
init({ tenantId: 1, tenantSlug: 'my-org', userId: 42 });

// Track an event anywhere in the app
track('page_view', 'Dashboard');
track('button_click', 'Events', { button: 'Create Event' });
track('filter_applied', 'Events', { filter: 'upcoming' });`;

const DASHBOARD_SDK = `// react_dashboard/src/lib/telemetry.js
import { init, track } from './lib/telemetry';

// In your auth context / after login:
init({ tenantId: instance.id, tenantSlug: instance.slug, userId: user.id });

// Track page views and actions:
track('page_view', 'EventPlanner');
track('rundown_block_added', 'EventPlanner', { block_type: 'talk' });
track('event_published', null, { event_id: 123 });`;

const MOBILE_SDK = `// selwyn_mobile/src/services/telemetry.js
import { init, setUser, track } from './services/telemetry';

// In App.js after app loads:
await init();

// After user logs in:
setUser({ userId: user.id, tenantId: instance.id, tenantSlug: instance.slug });

// Track events (queued offline, flushed when connected):
track('screen_view', 'EventDetail');
track('ticket_scanned', 'Scanner', { ticket_id: 'TKT-001', result: 'valid' });`;

const CURL_INGEST = `curl -X POST https://telemetry.selwynevents.com/api/v1/ingest/ \\
  -H "Content-Type: application/json" \\
  -H "X-Ingest-Key: sk-your-ingest-key-here" \\
  -d '{
    "events": [
      {
        "app": "portal",
        "tenant_id": 1,
        "tenant_slug": "my-org",
        "user_id": 42,
        "session_id": "sess_abc123",
        "event_type": "page_view",
        "screen": "Dashboard",
        "properties": { "referrer": "/login" }
      }
    ]
  }'`;

const CURL_STATS = `curl https://telemetry.selwynevents.com/api/v1/stats/ \\
  -H "Authorization: Token your-admin-token"`;

const CURL_EVENTS = `# List recent events, filtered by app
curl "https://telemetry.selwynevents.com/api/v1/events/?app=portal&ordering=-ts" \\
  -H "Authorization: Token your-admin-token"

# Filter by tenant
curl "https://telemetry.selwynevents.com/api/v1/events/?tenant_id=1" \\
  -H "Authorization: Token your-admin-token"`;

function ApiDocs() {
  return (
    <VStack spacing={8} align="stretch">
      <Box>
        <Heading size="lg" color="gray.800">API Documentation</Heading>
        <Text color="gray.500" mt={1}>How to send telemetry and query the data</Text>
      </Box>

      {/* Auth overview */}
      <Box bg="white" borderRadius="lg" p={6} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
        <Heading size="sm" color="gray.700" mb={3}>Authentication</Heading>
        <Text color="gray.600" fontSize="sm" mb={4}>
          The telemetry API uses two different auth mechanisms depending on the endpoint:
        </Text>
        <VStack align="stretch" spacing={3}>
          <HStack align="start" spacing={4}>
            <Badge colorScheme="purple" mt={0.5} flexShrink={0}>Ingest</Badge>
            <Box>
              <Text fontSize="sm" color="gray.700" fontWeight="medium">X-Ingest-Key header</Text>
              <Text fontSize="xs" color="gray.500">Used by SDKs to submit events. Create keys in the Ingest Keys section. No user login required.</Text>
            </Box>
          </HStack>
          <HStack align="start" spacing={4}>
            <Badge colorScheme="blue" mt={0.5} flexShrink={0}>Admin API</Badge>
            <Box>
              <Text fontSize="sm" color="gray.700" fontWeight="medium">Authorization: Token &lt;token&gt;</Text>
              <Text fontSize="xs" color="gray.500">Used to query events, stats, and manage ingest keys. Obtain a token via POST /api/v1/auth/login/.</Text>
            </Box>
          </HStack>
        </VStack>
      </Box>

      {/* Endpoints */}
      <Box>
        <Heading size="sm" color="gray.700" mb={4}>Endpoints</Heading>
        <Accordion allowMultiple>
          <EndpointCard
            method="POST"
            path="/api/v1/ingest/"
            description="Submit one or more telemetry events"
            auth="X-Ingest-Key: sk-your-key"
            request={`{
  "events": [
    {
      "app": "portal" | "dashboard" | "mobile",
      "tenant_id": 1,          // optional
      "tenant_slug": "my-org", // optional
      "user_id": 42,           // optional
      "session_id": "sess_x",  // optional
      "event_type": "page_view",
      "screen": "Dashboard",   // optional
      "properties": {}         // optional, any JSON
    }
  ]
}`}
            response={`{ "accepted": 1 }`}
          />
          <EndpointCard
            method="GET"
            path="/api/v1/stats/"
            description="Platform-wide usage statistics"
            auth="Authorization: Token <token>"
            response={`{
  "total": 12500,
  "last_24h": 340,
  "last_7d": 2100,
  "last_30d": 8900,
  "by_app": [{ "app": "portal", "count": 5000 }, ...],
  "top_events": [{ "event_type": "page_view", "count": 4200 }, ...],
  "dau_last_7d": [{ "date": "2026-05-21", "users": 38 }, ...],
  "hourly_last_24h": [{ "hour": "2026-05-27T10:00:00Z", "count": 12 }, ...],
  "top_tenants": [{ "tenant_id": 1, "tenant_slug": "my-org", "count": 3100 }, ...]
}`}
          />
          <EndpointCard
            method="GET"
            path="/api/v1/events/"
            description="Paginated list of raw events (100 per page)"
            auth="Authorization: Token <token>"
            request={`Query params:
  app         — portal | dashboard | mobile
  tenant_id   — integer
  user_id     — integer
  event_type  — exact match string
  ordering    — field name, prefix with - for desc (e.g. -ts)
  page        — page number`}
            response={`{
  "count": 12500,
  "next": "/api/v1/events/?page=2",
  "previous": null,
  "results": [
    {
      "id": 1,
      "ts": "2026-05-27T10:00:00Z",
      "app": "portal",
      "tenant_id": 1,
      "tenant_slug": "my-org",
      "user_id": 42,
      "session_id": "sess_abc",
      "event_type": "page_view",
      "screen": "Dashboard",
      "properties": {}
    }
  ]
}`}
          />
          <EndpointCard
            method="GET"
            path="/api/v1/ingest-keys/"
            description="List all ingest keys"
            auth="Authorization: Token <token>"
            response={`{
  "results": [
    {
      "id": 1,
      "name": "portal-production",
      "key": "sk-abc123...",
      "is_active": true,
      "created_at": "2026-05-01T00:00:00Z",
      "last_used_at": "2026-05-27T09:55:00Z"
    }
  ]
}`}
          />
          <EndpointCard
            method="POST"
            path="/api/v1/ingest-keys/"
            description="Create a new ingest key"
            auth="Authorization: Token <token>"
            request={`{
  "name": "portal-production",
  "key": "sk-your-generated-key"
}`}
            response={`{ "id": 2, "name": "portal-production", "key": "sk-...", "is_active": true }`}
          />
          <EndpointCard
            method="POST"
            path="/api/v1/auth/login/"
            description="Obtain an admin auth token"
            request={`{ "username": "admin", "password": "your-password" }`}
            response={`{ "token": "abc123..." }`}
          />
        </Accordion>
      </Box>

      <Divider />

      {/* SDK usage */}
      <Box>
        <Heading size="sm" color="gray.700" mb={1}>SDK Usage</Heading>
        <Text color="gray.500" fontSize="sm" mb={4}>
          Client SDKs queue events in memory and flush them to the ingest endpoint automatically.
          The mobile SDK additionally persists the queue offline via SecureStore.
        </Text>

        <Tabs colorScheme="purple" variant="enclosed">
          <TabList>
            <Tab fontSize="sm">Portal</Tab>
            <Tab fontSize="sm">Dashboard</Tab>
            <Tab fontSize="sm">Mobile</Tab>
            <Tab fontSize="sm">cURL</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pt={4}>
              <CodeBlock>{PORTAL_SDK}</CodeBlock>
              <Text fontSize="xs" color="gray.400" mt={2}>
                File: <Code fontSize="xs">portal/src/lib/telemetry.js</Code> — call <Code fontSize="xs">init()</Code> once after login, then <Code fontSize="xs">track()</Code> anywhere.
              </Text>
            </TabPanel>
            <TabPanel px={0} pt={4}>
              <CodeBlock>{DASHBOARD_SDK}</CodeBlock>
              <Text fontSize="xs" color="gray.400" mt={2}>
                File: <Code fontSize="xs">react_dashboard/src/lib/telemetry.js</Code>
              </Text>
            </TabPanel>
            <TabPanel px={0} pt={4}>
              <CodeBlock>{MOBILE_SDK}</CodeBlock>
              <Text fontSize="xs" color="gray.400" mt={2}>
                File: <Code fontSize="xs">selwyn_mobile/src/services/telemetry.js</Code> — <Code fontSize="xs">init()</Code> is async on mobile (loads persisted queue). Events are queued offline and flushed when connected.
              </Text>
            </TabPanel>
            <TabPanel px={0} pt={4}>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={2}>Submit events</Text>
                  <CodeBlock>{CURL_INGEST}</CodeBlock>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={2}>Get stats</Text>
                  <CodeBlock>{CURL_STATS}</CodeBlock>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={2}>Query events</Text>
                  <CodeBlock>{CURL_EVENTS}</CodeBlock>
                </Box>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Event types */}
      <Box bg="white" borderRadius="lg" p={6} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
        <Heading size="sm" color="gray.700" mb={3}>App identifiers</Heading>
        <Text color="gray.600" fontSize="sm" mb={4}>
          The <Code fontSize="sm">app</Code> field must be one of these three values:
        </Text>
        <HStack spacing={3} wrap="wrap">
          <Box>
            <Badge colorScheme="teal" fontSize="sm" px={3} py={1}>portal</Badge>
            <Text fontSize="xs" color="gray.500" mt={1}>Attendee-facing web portal</Text>
          </Box>
          <Box>
            <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>dashboard</Badge>
            <Text fontSize="xs" color="gray.500" mt={1}>Organiser dashboard</Text>
          </Box>
          <Box>
            <Badge colorScheme="orange" fontSize="sm" px={3} py={1}>mobile</Badge>
            <Text fontSize="xs" color="gray.500" mt={1}>iOS / Android mobile app</Text>
          </Box>
        </HStack>
      </Box>
    </VStack>
  );
}

export default ApiDocs;
